import { queryCache } from "react-query";
import { sortingMethods } from "../models/sortingOptions";
import storageConfig from "../config/storage";

const PAGE_SIZE = 20;

const isCityBlock = (housingCompany) => {
  return housingCompany.type === "???"; // need to figure out how to exclude city blocks
};

const applyPagination = (result, page) => {
  const totalPages = Math.ceil(result.items.length / PAGE_SIZE);
  let actualPage = page;
  if (actualPage >= totalPages) {
    actualPage = totalPages - 1; //last page
  }
  result.pagination = {
    more: false,
    page: actualPage,
    pageSize: PAGE_SIZE,
    total: result.items.length,
    totalPages: totalPages,
  };
  const startIndex = actualPage * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  if (endIndex < result.items.length - 1) {
    result.pagination.more = true;
  }
  if (startIndex < result.items.length) {
    result.items = result.items.slice(startIndex, endIndex);
  }
  return result;
};

const baseUrl = storageConfig.getStorageUrl();

const queryHeaders = { "content-type": "application/json" };

const getLocations = async () => {
  return fetch(baseUrl + "locations").then((response) => response.json());
};

const fetchApartments = (key, searchParams) => {
  return fetch(baseUrl + "query", {
    method: "POST",
    headers: queryHeaders,
    body: searchParams,
  })
    .then((response) => {
      return response.json();
    })
    .then((response) => {
      const result = {
        housingCompanies: response,
        singleApartments: [],
      };
      response.forEach((housingCompany) => {
        result.singleApartments = [
          ...result.singleApartments,
          ...housingCompany.apartments.map((apartment) => {
            apartment.construction = housingCompany.construction;
            apartment.availability = housingCompany.availability;
            apartment.showingTime = housingCompany.showingTime;
            apartment.housingCompanyName = housingCompany.name;
            apartment.marketingDate = housingCompany.marketingDate;
            apartment.disableReservations =
              housingCompany.disableReservations ?? false;
            return apartment;
          }),
        ];
      });
      return result;
    });
};

const getApartments = async (
  apartmentType,
  searchParams,
  sorting = "newest",
  page = 0
) => {
  return new Promise(async (resolve, reject) => {
    if (!queryCache.getQueryData(["query", searchParams])) {
      await queryCache.prefetchQuery(["query", searchParams], fetchApartments);
    }
    const queryResult = queryCache.getQueryData(["query", searchParams]);

    const result = {
      items: [],
      pagination: false,
    };

    if (apartmentType === "singleApartment") {
      const sortingMethod = sortingMethods.singleApartment[sorting];
      // Just sorting the results in place leads to inconsistent ordering
      // when first sorting by (e.g.) price and then by readyDate, as
      // most targets are equal by that metric. Therefore we sort a copy
      // of the data as a first-order fix, so the results are always
      // consistent.
      const unsortedResults = [...queryResult.singleApartments];
      result.items = unsortedResults.sort(sortingMethod);
    } else {
      const sortingMethod = sortingMethods.housingCompany[sorting];
      result.items = queryResult.housingCompanies
        .filter((housingCompany) => !isCityBlock(housingCompany))
        .sort(sortingMethod);
    }
    if (page !== false) {
      resolve(applyPagination(result, page));
    } else {
      resolve(result);
    }
  });
};

const getCompany = async (queryKey, companyKey) => {
  const housingCompany = await fetch(
    `${baseUrl}company/${companyKey}`
  ).then((response) => response.json());
  const apartments = await fetch(`${baseUrl}company/${companyKey}/apartments`)
    .then((response) => response.json())
    .then((apartments) =>
      apartments.filter((apartment) => apartment.status.current !== "Myyty")
    )
    .then((apartments) => {
      apartments.forEach((apartment) => {
        apartment.construction = housingCompany.construction;
        apartment.availability = housingCompany.availability;
        apartment.showingTime = housingCompany.showingTime;
        apartment.housingCompanyName = housingCompany.name;
        apartment.marketingDate = housingCompany.marketingDate;
        apartment.disableReservations =
          housingCompany.disableReservations ?? false;
      });
      return apartments;
    });
  housingCompany.apartments = apartments;
  return housingCompany;
};

const getApartment = async (queryKey, apartmentKey) => {
  const apartment = await fetch(
    `${baseUrl}apartment/${apartmentKey}`
  ).then((response) => response.json());
  return apartment;
};

export { getLocations, getApartments, getCompany, getApartment };
